import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { Connection, ConnectorProfile } from '@/shared/api/generated/catalog';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SecretField, TextField } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';

import { CONNECTOR_LABEL } from '@/entities/connection';

import {
  credentialPayload,
  fieldsFor,
  missingFields,
  offersMechanismChoice,
} from '../model/registration';
import type { Mechanism } from '../model/registration';

/* Replacing the credential behind an existing source.
 *
 * **The fields come from `GET /v1/catalog/connectors`, through the same `fieldsFor` the registration
 * wizard uses.** Not a second form: five providers want five field sets and one of them is a secret,
 * and a hand-written credential form per provider is how a field comes to be asked for and silently
 * ignored — or required by the API and absent from the screen. Reusing the model is also what keeps
 * `secret_fields` read rather than guessed, so a Databricks `token` renders as a password field
 * despite containing none of the obvious words.
 *
 * **Every rule the registration form holds about secret handling holds here unchanged**, and
 * `SecretField` is what implements them: `type="password"`, `autoComplete="new-password"`, spellcheck
 * and autocapitalise off, and — the one that is not obvious — **uncontrolled**, because a controlled
 * input mirrors its value into the DOM `value` attribute and the credential then appears in
 * `outerHTML`. The values live in this component's state and nowhere else: no localStorage, no URL, no
 * query-cache residue (the caller resets the mutation).
 *
 * **`resetKey` remounts every field on a successful save**, which is how the inputs are cleared. A
 * `value=""` would not clear an uncontrolled input, and leaving the material in the DOM for the life
 * of the tab is exactly what the uncontrolled choice above exists to avoid.
 *
 * **The dialog says what saving costs before it is saved**, because it is not obvious: the source
 * returns to never-verified, so whatever verdict was on screen a second ago stops being true. A
 * reader who is told that afterwards reasonably concludes something broke.
 */
export interface ReplaceCredentialDialogProps {
  readonly open: boolean;
  readonly connection: Connection;
  /** The provider's field catalogue, or `undefined` when `/connectors` could not be read. */
  readonly profile: ConnectorProfile | undefined;
  readonly busy: boolean;
  /** The server's refusal, host-written and echoing no submitted value. */
  readonly error: string | undefined;
  readonly onSubmit: (credentials: Readonly<Record<string, string>>) => void;
  readonly onClose: () => void;
}

export function ReplaceCredentialDialog({
  open,
  connection,
  profile,
  busy,
  error,
  onSubmit,
  onClose,
}: ReplaceCredentialDialogProps): ReactElement | null {
  const { t } = useTranslation();
  const [mechanism, setMechanism] = useState<Mechanism>('password');
  const [values, setValues] = useState<Record<string, string>>({});
  /* Bumped on every provider or mechanism change so the secret inputs remount empty. A value typed
   * under one mechanism must not be submitted under the other — the domain refuses a credential
   * carrying both, and this is what stops the form producing one. */
  const [resetKey, setResetKey] = useState(0);

  const fields = useMemo(
    () => (profile === undefined ? [] : fieldsFor(profile, mechanism)),
    [profile, mechanism],
  );
  const offersChoice = profile !== undefined && offersMechanismChoice(profile);
  const missing = missingFields(fields, values);

  if (!open) return null;

  if (profile === undefined) {
    /* **Refused rather than rendered with no fields.** A form that could not read the field
     * catalogue cannot know what this provider needs, so an empty one would invite a reader to
     * submit nothing and be refused by the server for a reason that is about our request rather
     * than their credential. */
    return (
      <Modal
        open={open}
        title={t('connectionDetail.credential.title')}
        onClose={onClose}
        size="narrow"
      >
        <Alert tone="error" title={t('connections.register.catalogueFailed')}>
          {t('connections.register.catalogueFailedBody')}
        </Alert>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title={t('connectionDetail.credential.title')}
      hint={t('connectionDetail.credential.hint', {
        connector: CONNECTOR_LABEL[connection.connector_type],
        name: connection.name,
      })}
      onClose={onClose}
      size="narrow"
    >
      <p className="lq-source__note">{t('connectionDetail.credential.consequence')}</p>

      {offersChoice ? (
        <fieldset className="lq-mechanism">
          <legend className="lq-mechanism__legend">
            {t('connections.register.mechanism')}
          </legend>
          {(['password', 'private_key'] as const).map((choice) => (
            <label key={choice} className="lq-mechanism__option">
              {/* A real `input type="radio"`, so arrow keys work — the reference implementation's
                  rule, and the reason the provider chooser uses one too. */}
              <input
                type="radio"
                name="replace-mechanism"
                checked={mechanism === choice}
                onChange={() => {
                  setMechanism(choice);
                  setValues({});
                  setResetKey((key) => key + 1);
                }}
              />
              {t(`connections.register.mechanismOption.${choice}`)}
            </label>
          ))}
        </fieldset>
      ) : null}

      {fields.map((field) =>
        field.secret ? (
          <SecretField
            key={`${field.name}-${String(resetKey)}`}
            label={t(`connections.field.${field.name}`, { defaultValue: field.name })}
            required={field.required}
            multiline={field.multiline}
            resetKey={String(resetKey)}
            onChange={(value) => {
              setValues((current) => ({ ...current, [field.name]: value }));
            }}
          />
        ) : (
          <TextField
            key={`${field.name}-${String(resetKey)}`}
            label={t(`connections.field.${field.name}`, { defaultValue: field.name })}
            required={field.required}
            value={values[field.name] ?? ''}
            onChange={(value) => {
              setValues((current) => ({ ...current, [field.name]: value }));
            }}
          />
        ),
      )}

      {/* The server's refusal, rendered inside the dialog that asked the question. Closing and
          putting it on the page underneath would put the answer somewhere the reader has stopped
          looking — `ConfirmDialog`'s recorded lesson. */}
      {error !== undefined ? (
        <Alert tone="error" title={t('connections.register.refused')}>
          {error}
        </Alert>
      ) : null}

      {/* `.lq-confirm__actions` rather than a new class: it is the kit's action row and it
          already argues the Cancel-then-confirm ordering from a `dir`-aware flex direction.
          A second declaration would be the duplicate-class family §18 records six times. */}
      <div className="lq-confirm__actions">
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          tone="primary"
          busy={busy}
          disabled={missing.length > 0}
          title={
            missing.length > 0
              ? t('connections.register.missing', { fields: missing.join(', ') })
              : undefined
          }
          onClick={() => {
            /* `credentialPayload` rather than the raw state: it trims and drops empties, so an
               optional field somebody typed into and cleared is absent rather than a stored empty
               value the caller did not mean to set. The same function the registration form
               submits through, so the two cannot disagree about what an empty field means. */
            onSubmit(credentialPayload(fields, values));
          }}
        >
          {t('connectionDetail.credential.submit')}
        </Button>
      </div>
    </Modal>
  );
}
